import { Injectable } from '@angular/core';
import { ItemGroup } from './models/itemGroup';
import { FacilityItemInformation } from './models/facilityItemInformation';
import { ItemGroupEntity } from './models/itemGroupEntity';

@Injectable({ providedIn: 'root' })
export class ItemGroupHelpers {
  public static mapItemsToMenuItemGroup(items: FacilityItemInformation[]): Map<string, ItemGroup> {
    const menuItemGroups = new Map<string, ItemGroup>();
    items.forEach((item) => {
      let itemGroup = menuItemGroups.get(item.externalItemNumber);
      itemGroup = this.upsertItemGroup(item, itemGroup);
      menuItemGroups.set(item.externalItemNumber ?? item.commerceItemNumber, itemGroup);
    });

    return menuItemGroups;
  }

  public static getCommodityItemGroups(items: FacilityItemInformation[]): ItemGroupEntity[] {
    const commodityItems = items.filter((i) => i.commodityGroupId !== undefined && i.commodityGroupId > 0);
    return ItemGroupHelpers.mapRawItemsToItemGroupEntity(commodityItems);
  }

  public static getNonCommodityItemGroups(items: FacilityItemInformation[]): ItemGroupEntity[] {
    const nonCommodityItems = items.filter((i) => i.commodityGroupId === undefined || i.commodityGroupId <= 0);
    const menuItemGroups = ItemGroupHelpers.mapItemsToMenuItemGroup(nonCommodityItems);
    const itemGroups = ItemGroupHelpers.mapItemGroupsMapToItemGroupsArray(menuItemGroups);
    return ItemGroupHelpers.mapItemGroupsToItemGroupEntities(itemGroups);
  }

  private static mapRawItemsToItemGroupEntity(items: FacilityItemInformation[]): ItemGroupEntity[] {
    const commodityGroups = new Map<number, FacilityItemInformation[]>();
    items.forEach((item) => {
      let commodityGroup = commodityGroups.get(item.commodityGroupId!);
      if (commodityGroup !== undefined) {
        commodityGroup.push(item);
      } else {
        commodityGroup = [item];
      }
      commodityGroups.set(item.commodityGroupId!, commodityGroup);
    });

    const cleanedItemGroups: ItemGroupEntity[] = [];
    for (const key of commodityGroups.keys()) {
      const commodityGroupItems = commodityGroups.get(key);
      if (!commodityGroupItems) {
        continue;
      }
      const baselineItem =
        commodityGroupItems.find((item) => item.commodityGroupUnitQuantity === 1) || commodityGroupItems[0];
      const commodityGroupUnitQuantity = baselineItem.commodityGroupUnitQuantity;
      const baselineSellableQuantity = this.getBaselineSellableQuantity(
        baselineItem.sellableQuantity,
        baselineItem.commodityGroupUnitQuantity,
      );
      const menuItemGroups = this.mapItemsToMenuItemGroup(commodityGroups.get(key)!);
      const itemGroups = this.mapItemGroupsMapToItemGroupsArray(menuItemGroups);

      const totalSoldQuantity = itemGroups.reduce((acc, group) => {
        return (
          acc + this.getTotalQuantityByMultiplier(group.totalSoldQuantity, group.items[0].commodityGroupUnitQuantity)
        );
      }, 0);

      // Calculate availability error for each item group within this commodity group
      itemGroups.forEach((group) => {
        group.availabilityError = this.calculateAvailabilityError(group, baselineSellableQuantity, totalSoldQuantity);
      });

      // Calculate out of sync error for each item group within this commodity group
      itemGroups.forEach((group) => {
        group.isOutOfSync = this.calculateOutOfSyncError(group.items);
      });

      const allItemsInEntity = itemGroups.flatMap((group) => group.items);
      const entityIsOutOfSync =
        itemGroups.some((group) => group.isOutOfSync) || this.calculateOutOfSyncError(allItemsInEntity);

      cleanedItemGroups.push({
        commodityGroupName: baselineItem.commodityGroupName ?? '',
        isCommodityGroup: true,
        itemGroups: itemGroups,
        itemNumber: itemGroups[0].itemNumber,
        totalSoldQuantity: totalSoldQuantity,
        totalSellableQuantity: itemGroups.reduce((acc, group) => {
          const totalQuantity = this.getTotalQuantityByMultiplier(
            group.totalSellableQuantity,
            group.items[0].commodityGroupUnitQuantity,
          );
          return acc >= totalQuantity ? acc : totalQuantity;
        }, 0),
        commodityGroupUnitQuantity: commodityGroupUnitQuantity,
        totalSellableQuantityBaseline: baselineSellableQuantity,
        isOutOfSync: entityIsOutOfSync,
      } as ItemGroupEntity);
    }
    return cleanedItemGroups;
  }

  private static getBaselineSellableQuantity(value: number, multiplier: number | undefined | null): number {
    if (multiplier === 1 || multiplier === null || multiplier === undefined) {
      return value;
    }

    return value * multiplier;
  }

  private static getTotalQuantityByMultiplier(value: number, multiplier: number | undefined | null): number {
    return value * (multiplier ?? 1);
  }

  private static calculateAvailabilityError(
    itemGroup: ItemGroup,
    baselineSellableQuantity?: number,
    totalSoldQuantity?: number,
  ): boolean {
    const availableItems = itemGroup.items.filter((i) => i.isAvailable);
    const unavailableItems = itemGroup.items.filter((i) => !i.isAvailable);

    // Check for mixed availability states
    const hasMixedAvailability = availableItems.length > 0 && unavailableItems.length > 0;

    // Check for commodity group sold out condition - only if inventory tracking is enabled
    let hasSoldOutError = false;
    if (baselineSellableQuantity !== undefined && totalSoldQuantity !== undefined) {
      const hasInventoryTracking = itemGroup.items.some((i) => i.isSellableQuantityEnabled);
      if (hasInventoryTracking) {
        const shouldBeSoldOut =
          baselineSellableQuantity - totalSoldQuantity < (itemGroup.items[0]?.commodityGroupUnitQuantity ?? 1);
        hasSoldOutError = shouldBeSoldOut && !itemGroup.availabilityExceptionFromInventoryManagement;
      }
    }

    // Check for inventory quantity error (using itemGroup's own properties)
    const hasInventoryQuantityError =
      itemGroup.totalSoldQuantity > itemGroup.totalSellableQuantity
      && !itemGroup.availabilityExceptionFromInventoryManagement;

    // Check for availability/unavailability mismatch
    const hasAvailabilityMismatch = availableItems.length > 0 && !itemGroup.isAvailable;

    return hasMixedAvailability || hasSoldOutError || hasInventoryQuantityError || hasAvailabilityMismatch;
  }

  public static mapItemGroupsToItemGroupEntities(itemGroups: ItemGroup[]): ItemGroupEntity[] {
    const commodityGroups: ItemGroupEntity[] = [];
    for (const itemGroup of itemGroups) {
      commodityGroups.push({
        commodityGroupId: undefined,
        commodityGroupName: undefined,
        totalSellableQuantity: itemGroup.totalSellableQuantity,
        totalSoldQuantity: itemGroup.totalSoldQuantity,
        isCommodityGroup: false,
        itemGroups: [itemGroup],
        isOutOfSync: itemGroup.isOutOfSync,
        itemNumber: itemGroup.itemNumber,
        commodityGroupUnitQuantity: 1,
      } as ItemGroupEntity);
    }
    return commodityGroups;
  }

  private static upsertItemGroup(item: FacilityItemInformation, itemGroup: ItemGroup | undefined): ItemGroup {
    if (itemGroup !== undefined) {
      itemGroup.isExternalItemNumberGroup = true;
      itemGroup.items.push(item);
      itemGroup.itemNumber = item.externalItemNumber;

      // Check for mixed availability states
      const availableItems: FacilityItemInformation[] = [];
      const unavailableItems: FacilityItemInformation[] = [];
      for (const i of itemGroup.items) {
        if (i.isAvailable) {
          availableItems.push(i);
        } else {
          unavailableItems.push(i);
        }
      }

      // isAvailable is false if ANY item is unavailable
      itemGroup.isAvailable = unavailableItems.length === 0;

      // Calculate availability error using the helper method
      itemGroup.availabilityError = this.calculateAvailabilityError(itemGroup);

      // Calculate out of sync error using the helper method
      itemGroup.isOutOfSync = this.calculateOutOfSyncError(itemGroup.items);

      itemGroup.availabilityExceptionFromInventoryManagement = item.isInventoryManagementException;
      itemGroup.totalSellableQuantity = item.sellableQuantity;
      itemGroup.totalSoldQuantity = itemGroup.totalSoldQuantity + (item.soldQuantity || 0);
    } else {
      itemGroup = {
        isExternalItemNumberGroup: false,
        itemNumber: item.externalItemNumber ?? item.commerceItemNumber,
        items: [item],
        isAvailable: item.isAvailable,
        availabilityExceptionFromInventoryManagement: item.isInventoryManagementException,
        totalSellableQuantity: item.sellableQuantity,
        totalSoldQuantity: item.soldQuantity || 0,
        availabilityError: (item.soldQuantity || 0) > item.sellableQuantity && !item.isInventoryManagementException,
        isOutOfSync: false,
      } as ItemGroup;
    }
    itemGroup.inventorySoldOut = !itemGroup.isAvailable && itemGroup.availabilityExceptionFromInventoryManagement;

    return itemGroup;
  }

  private static sortItemGroups(itemGroups: ItemGroup[]): ItemGroup[] {
    return itemGroups.sort((a, b) => Number.parseInt(a.itemNumber) - Number.parseInt(b.itemNumber));
  }

  public static mapItemGroupsMapToItemGroupsArray(menuItemGroups: Map<string, ItemGroup>): ItemGroup[] {
    let filteredItemGroups = Array.from(menuItemGroups, ([name, value]) => value);
    filteredItemGroups = this.sortItemGroups(filteredItemGroups);
    return filteredItemGroups;
  }

  public static combineItemGroupsWithCommodityItems(
    itemGroups: ItemGroup[],
    commodityItems: FacilityItemInformation[],
  ): ItemGroupEntity[] {
    const commodityItemGroups = this.mapRawItemsToItemGroupEntity(commodityItems);
    const nonCommodityGroups = this.mapItemGroupsToItemGroupEntities(itemGroups);
    return Array.prototype
      .concat(commodityItemGroups, nonCommodityGroups)
      .sort((a, b) => parseInt(a.itemNumber) - parseInt(b.itemNumber));
  }

  public static calculateOutOfSyncError(items: FacilityItemInformation[]): boolean {
    if (items.length <= 1) {
      return false;
    }

    const firstItem = items[0];

    const normalizeDate = (d?: Date | null): string => (d ? new Date(d).toISOString() : '');
    const normalizeHandoffTypes = (types?: string[] | null): string =>
      JSON.stringify((types ?? []).slice().sort((a, b) => a.localeCompare(b)));

    return items.slice(1).some((item) => {
      const firstItemSellable = (firstItem.sellableQuantity ?? 0) * (firstItem.commodityGroupUnitQuantity ?? 1);
      const itemSellable = (item.sellableQuantity ?? 0) * (item.commodityGroupUnitQuantity ?? 1);

      return (
        item.departmentName !== firstItem.departmentName
        || item.leadMinutes !== firstItem.leadMinutes
        || item.prepMinutes !== firstItem.prepMinutes
        || (item.postPrepMinutes ?? 0) !== (firstItem.postPrepMinutes ?? 0)
        || normalizeDate(item.startPickupDate) !== normalizeDate(firstItem.startPickupDate)
        || normalizeDate(item.endPickupDate) !== normalizeDate(firstItem.endPickupDate)
        || normalizeDate(item.startSoldQuantityDate) !== normalizeDate(firstItem.startSoldQuantityDate)
        || normalizeDate(item.endSoldQuantityDate) !== normalizeDate(firstItem.endSoldQuantityDate)
        || item.isSellableQuantityEnabled !== firstItem.isSellableQuantityEnabled
        || normalizeHandoffTypes(item.allowedHandoffTypes) !== normalizeHandoffTypes(firstItem.allowedHandoffTypes)
        || (item.isSellableQuantityEnabled && itemSellable !== firstItemSellable)
      );
    });
  }
}
